// JSON-LD schema builders for Ministry of Jesus.
// Returns plain JS objects that are serialized to <script type="application/ld+json">.
// Reference: https://schema.org/Church , https://schema.org/Event , https://schema.org/FAQPage

import { SITE_URL, SITE_NAME, SITE_LOGO, BUSINESS, absoluteUrl } from './seoConfig';

// Map English weekday names to schema.org DayOfWeek URIs
const DAY_URI = {
  Sunday: 'https://schema.org/Sunday',
  Monday: 'https://schema.org/Monday',
  Tuesday: 'https://schema.org/Tuesday',
  Wednesday: 'https://schema.org/Wednesday',
  Thursday: 'https://schema.org/Thursday',
  Friday: 'https://schema.org/Friday',
  Saturday: 'https://schema.org/Saturday',
};

function postalAddress() {
  return {
    '@type': 'PostalAddress',
    streetAddress: BUSINESS.address.streetAddress,
    addressLocality: BUSINESS.address.addressLocality,
    addressRegion: BUSINESS.address.addressRegion,
    postalCode: BUSINESS.address.postalCode,
    addressCountry: BUSINESS.address.addressCountry,
  };
}

function areaServed() {
  return BUSINESS.areaServed.map((a) => ({
    '@type': a.type || 'AdministrativeArea',
    name: a.name,
  }));
}

function openingHoursSpec() {
  return BUSINESS.services.map((s) => ({
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: DAY_URI[s.day] || s.day,
    opens: s.openTime,
    closes: s.closeTime,
    name: s.name,
  }));
}

// Organization (used across the site)
export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: BUSINESS.name,
    legalName: BUSINESS.legalName,
    url: SITE_URL,
    logo: SITE_LOGO,
    email: `mailto:${BUSINESS.email}`,
    telephone: BUSINESS.phone,
    description: BUSINESS.description,
    sameAs: BUSINESS.sameAs,
    founder: { '@type': 'Person', name: BUSINESS.founder },
    foundingLocation: BUSINESS.foundingLocation,
    address: postalAddress(),
    areaServed: areaServed(),
  };
}

// Church / Local Business — strongest signal for local SEO
export function churchSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': ['Church', 'PlaceOfWorship', 'LocalBusiness'],
    '@id': `${SITE_URL}/#church`,
    name: BUSINESS.name,
    alternateName: 'Ministry of Jesus Pentecostal Church',
    url: SITE_URL,
    image: SITE_LOGO,
    logo: SITE_LOGO,
    telephone: BUSINESS.phone,
    email: `mailto:${BUSINESS.email}`,
    priceRange: 'Free',
    description: BUSINESS.description,
    address: postalAddress(),
    geo: {
      '@type': 'GeoCoordinates',
      latitude: BUSINESS.geo.latitude,
      longitude: BUSINESS.geo.longitude,
    },
    areaServed: areaServed(),
    sameAs: BUSINESS.sameAs,
    openingHoursSpecification: openingHoursSpec(),
    // Schema.org allows specifying religion / denomination informally
    knowsAbout: ['Pentecostal Christianity', 'Bible teaching', 'Prayer ministry', 'Healing ministry'],
    parentOrganization: { '@id': `${SITE_URL}/#organization` },
  };
}

// Website (with SearchAction not enabled because no on-site search)
export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: SITE_URL,
    name: SITE_NAME,
    inLanguage: 'en-IE',
    publisher: { '@id': `${SITE_URL}/#organization` },
  };
}

// Breadcrumb list
export function breadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: it.name,
      item: absoluteUrl(it.path),
    })),
  };
}

// Recurring service / event schema for one of BUSINESS.services entries.
// Used on Home + Live + Morning Prayer pages.
// NOTE: Google's Event rich-result validator requires an ISO 8601 `startDate`
// even when the primary structure is a repeating `eventSchedule`. We compute
// the next occurrence of the given weekday so validators are satisfied.
function _nextOccurrenceISO(day, hhmm) {
  const dayIndex = {
    Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
    Thursday: 4, Friday: 5, Saturday: 6,
  }[day] ?? 0;
  const [h, m] = (hhmm || '10:00').split(':').map((n) => parseInt(n, 10));
  const now = new Date();
  const nowUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const diff = (dayIndex - nowUTC.getUTCDay() + 7) % 7;
  const target = new Date(nowUTC.getTime() + diff * 86400000);
  target.setUTCHours(h, m, 0, 0);
  return target.toISOString();
}

export function recurringServiceEvent(svc) {
  const startISO = _nextOccurrenceISO(svc.day, svc.openTime);
  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: `${svc.name} — Ministry of Jesus`,
    description: svc.description,
    // Required by Google rich results, even for recurring events.
    startDate: startISO,
    eventSchedule: {
      '@type': 'Schedule',
      repeatFrequency: 'P1W',
      byDay: DAY_URI[svc.day] || svc.day,
      startTime: svc.openTime,
      endTime: svc.closeTime,
      scheduleTimezone: 'Europe/Dublin',
    },
    eventAttendanceMode: 'https://schema.org/MixedEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: [
      {
        '@type': 'Place',
        name: BUSINESS.name,
        address: postalAddress(),
      },
      {
        '@type': 'VirtualLocation',
        url: 'https://www.youtube.com/@MinistryofJesus/streams',
      },
    ],
    organizer: { '@id': `${SITE_URL}/#organization`, name: BUSINESS.name, url: SITE_URL },
    isAccessibleForFree: true,
    image: [SITE_LOGO],
  };
}

// FAQ schema (for home page local SEO Q&A block)
export function faqSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((q) => ({
      '@type': 'Question',
      name: q.q,
      acceptedAnswer: { '@type': 'Answer', text: q.a },
    })),
  };
}
